import { first, flatten } from 'underscore';
import Promise from 'bluebird';
import { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Campaign, AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import {
  getBulkUploadErrorType,
  ERROR_ENTITY,
  loadAdGroupForBudget,
  loadCampaign,
  loadBundleCountries,
  modelToViewModelForBulkErrors,
  modelToViewModelForCampaign,
  removeReferencedEntityInvalidIdErrors,
  readLocationCriterions,
  readScheduleCriterions,
  createOrUpdateCampaign,
  loadKeywords,
  KEYWORDS_SHOW_COUNT,
  loadAds,
} from '@bingads-webui-campaign-react/search-campaign-data';
import { useWizardService, } from '@bingads-webui-react/wizard';

const bulkUpsertCampaignSettingsEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

export const useSummaryRepository = () => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;
  const { state } = useWizardService();
  const serverContext = useContext(ServerContext);

  const {
    campaignId,
    adGroupId,
    websites: savedWebsites,
  } = state.context.wizardData;

  const load = useCallback(() => {
    const loadPromises = {
      campaign: campaignId ? loadCampaign(cid, aid, campaignId) : Promise.resolve({}),
      adGroup: adGroupId ? loadAdGroupForBudget(cid, aid, campaignId, adGroupId, i18n) : Promise.resolve({}),
      locationTargets: campaignId ? readLocationCriterions(cid, aid, campaignId) : Promise.resolve({}),
      bundleCountriesAndLocationCriterions: loadBundleCountries({ cid, aid, serverContext }),
      scheduleCriterion: campaignId ? readScheduleCriterions(cid, aid, campaignId) : Promise.resolve(null),
      ads: adGroupId ? loadAds({
        cid, aid, campaignId, adGroupId,
      }) : Promise.resolve([]),
      keywords: adGroupId ? loadKeywords(cid, aid, campaignId, adGroupId, KEYWORDS_SHOW_COUNT) : Promise.resolve({}),
    };

    return Promise.props(loadPromises)
      .then(({
        campaign: rawCampaign,
        adGroup,
        locationTargets,
        bundleCountriesAndLocationCriterions,
        scheduleCriterion,
        ads,
        keywords,
      }) => {
        const campaign = campaignId ? modelToViewModelForCampaign({
          campaignModel: rawCampaign,
          locationCriterionsModel: locationTargets ? locationTargets.value : [],
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
          scheduleCriterionModel: scheduleCriterion ? scheduleCriterion.value : null,
        }) : rawCampaign;
        return Promise.resolve({
          campaign,
          adGroup,
          ads: first(ads) || {},
          keywords: keywords ? { value: keywords.value, total: keywords['@odata.count'] } : {},
          websites: savedWebsites,
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load summary failed: ${error}`);
        activity.signal(`onSummaryLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, campaignId, cid, aid, adGroupId, i18n, serverContext, savedWebsites]);


  const save = useCallback(({ data }) => {
    const { isPublishing } = state.context.wizardData;

    if (isPublishing) {
      const model = {
        Id: data.campaign.Id,
        Status: 'Active',
        AdGroups: [{
          Id: data.adGroup.Id,
          CampaignName: data.campaign.Name,
          Status: 'Active',
        }],
      };
      return new Promise((resolve, reject) => {
        createOrUpdateCampaign(model)
          .then(res => resolve({
            campaign: {
              ...res,
            },
            adGroup: {
              ...res.AdGroups[0],
            },
          }))
          .catch((res) => {
            // eslint-disable-next-line prefer-promise-reject-errors
            const serverErrors = modelToViewModelForBulkErrors(
              removeReferencedEntityInvalidIdErrors(res.errors),
              bulkUpsertCampaignSettingsEntityErrorMap,
              scenario.name,
              i18n,
              activity
            );
            // eslint-disable-next-line no-console
            console.error(`Save summary failed: ${res}`);
            activity.signal(`onSummarySaveFailed - ${JSON.stringify({
              data,
              error: res,
            })}`);
            // eslint-disable-next-line prefer-promise-reject-errors
            return reject({
              error: {
                serverErrors: flatten(serverErrors.filter(val => val !== null)),
              },
            });
          });
      });
    }

    // no update necessary if not publishing.
    return Promise.resolve({
      campaign: data.campaign,
      adGroup: data.adGroup,
    });
  }, [activity, i18n, scenario.name, state.context.wizardData]);

  return {
    load,
    save,
  };
};
